/**
 * AdCreativeEditor — 3-Schritt-Wizard zum Bearbeiten einer Meta-Anzeige:
 * ① Texte (KI-Vorschlaege + A/B-Test) → ② Bild (Picker + KI-Empfehlung) → ③ FB/Insta-Vorschau & Speichern.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Sparkles, ArrowLeft, ArrowRight, Check, ImageIcon, FlaskConical, Star, Heart, MessageCircle, Send, Bookmark } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Texte", "Bild", "Vorschau"];

export default function AdCreativeEditor({ ad, companyId, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ message: "", headline: "", description: "", link: "", media_id: "keep" });
  const [currentPicture, setCurrentPicture] = useState("");
  const [media, setMedia] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [bestMedia, setBestMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [abCreating, setAbCreating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [previewTab, setPreviewTab] = useState("fb");
  const blobUrls = useRef([]);

  useEffect(() => {
    if (!ad) return;
    setLoading(true);
    setStep(1);
    setSuggestions(null);
    Promise.all([
      axios.get(`${API}/ads/ads/${ad.id}/creative`, { params: { company_id: companyId }, withCredentials: true }),
      axios.get(`${API}/media`, { params: { company_id: companyId }, withCredentials: true }),
    ]).then(([c, m]) => {
      setF({ message: c.data.message, headline: c.data.headline, description: c.data.description, link: c.data.link, media_id: "keep" });
      setCurrentPicture(c.data.picture || "");
      setMedia((m.data || []).filter((x) => (x.content_type || x.file_type || "").startsWith("image")));
    }).catch(() => toast.error("Anzeige konnte nicht geladen werden"))
      .finally(() => setLoading(false));
    axios.get(`${API}/ads/ai/best-media`, { params: { company_id: companyId }, withCredentials: true })
      .then((r) => setBestMedia(r.data.ranking || [])).catch(() => {});
  }, [ad, companyId]);

  useEffect(() => {
    media.slice(0, 30).forEach((m) => {
      if (thumbs[m.id]) return;
      axios.get(`${API}/media/thumb/${m.id}`, { responseType: "blob", withCredentials: true })
        .then((r) => {
          const u = URL.createObjectURL(r.data);
          blobUrls.current.push(u);
          setThumbs((p) => ({ ...p, [m.id]: u }));
        }).catch(() => {});
    });
  }, [media]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { blobUrls.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const getSuggestions = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/ads/ai/creative-suggestions`, {
        company_id: companyId, message: f.message, headline: f.headline,
        description: f.description, link: f.link, media_id: f.media_id,
      }, { withCredentials: true });
      setSuggestions(r.data.suggestions || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "KI-Vorschläge fehlgeschlagen"); }
    setAiLoading(false);
  };

  const applySuggestion = (s) => {
    setF((p) => ({ ...p, message: s.primary_text, headline: s.headline, description: s.description }));
    setSuggestions(null);
    toast.success("Vorschlag übernommen — du kannst ihn noch anpassen");
  };

  const createAbVariants = async () => {
    setAbCreating(true);
    try {
      const r = await axios.post(`${API}/ads/ads/${ad.id}/ab-variants`, {
        company_id: companyId, variants: suggestions, link: f.link, media_id: f.media_id,
      }, { withCredentials: true });
      toast.success(r.data.message, { duration: 9000 });
      setSuggestions(null);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "A/B-Erstellung fehlgeschlagen"); }
    setAbCreating(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/ads/ads/${ad.id}/creative`, {
        company_id: companyId, ...f, media_id: f.media_id === "keep" ? "" : f.media_id,
      }, { withCredentials: true });
      toast.success(r.data.message);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setSaving(false);
  };

  const clicksMap = Object.fromEntries(bestMedia.map((b) => [b.media_id, b.clicks]));
  const topPickId = bestMedia[0]?.clicks > 0 ? bestMedia[0].media_id : null;
  const sortedMedia = [...media].sort((a, b) => (clicksMap[b.id] || 0) - (clicksMap[a.id] || 0));
  const previewImg = f.media_id !== "keep" ? thumbs[f.media_id] : currentPicture;
  const domain = (() => { try { return new URL(f.link).hostname; } catch { return f.link; } })();
  const textsOk = !!f.message && f.link.startsWith("http");

  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ad-creative-editor-dialog">
        <DialogHeader><DialogTitle className="text-base">Anzeige bearbeiten — {ad?.name}</DialogTitle></DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1" data-testid="wizard-steps">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-1 flex-1">
              <button type="button" onClick={() => (i + 1 < step || (i + 1 === 2 && textsOk) || i + 1 === 1) && setStep(i + 1)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full transition-colors ${
                  step === i + 1 ? "bg-blue-600 text-white" : step > i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
                data-testid={`wizard-step-${i + 1}`}>
                <span className="w-4 h-4 rounded-full bg-white/25 flex items-center justify-center text-[10px] font-bold">
                  {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                {label}
              </button>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-emerald-300" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {loading ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <>
            {step === 1 && (
              <div className="space-y-3" data-testid="wizard-texts-step">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Primärtext</Label>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-violet-700 border-violet-300 hover:bg-violet-50"
                    onClick={getSuggestions} disabled={aiLoading} data-testid="creative-ai-btn">
                    {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    KI-Vorschläge
                  </Button>
                </div>
                <Textarea rows={4} value={f.message} onChange={(e) => setF((s) => ({ ...s, message: e.target.value }))} data-testid="creative-message-input" />
                {suggestions && (
                  <div className="space-y-2 border border-violet-200 bg-violet-50/50 rounded-lg p-2.5" data-testid="ai-suggestions-box">
                    <p className="text-[11px] font-semibold text-violet-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> KI-Vorschläge — klicke zum Übernehmen</p>
                    {suggestions.map((s, i) => (
                      <button key={i} type="button" onClick={() => applySuggestion(s)}
                        className="w-full text-left bg-white border rounded-md p-2 hover:border-violet-400 transition-colors"
                        data-testid={`ai-suggestion-${i}`}>
                        <p className="text-xs whitespace-pre-wrap line-clamp-3">{s.primary_text}</p>
                        <p className="text-[11px] text-slate-500 mt-1"><b>{s.headline}</b>{s.description ? ` · ${s.description}` : ""}</p>
                      </button>
                    ))}
                    {suggestions.length >= 2 && (
                      <Button size="sm" variant="outline" className="w-full h-8 text-xs gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={createAbVariants} disabled={abCreating || !textsOk} data-testid="ab-variants-btn"
                        title="Erstellt pro Vorschlag eine pausierte Anzeige im selben Ad-Set — Meta misst dann, welche am besten läuft">
                        {abCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
                        Alle {suggestions.length} als A/B-Test-Anzeigen erstellen (pausiert)
                      </Button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Headline (max 40)</Label>
                    <Input className="h-8" maxLength={40} value={f.headline} onChange={(e) => setF((s) => ({ ...s, headline: e.target.value }))} data-testid="creative-headline-input" /></div>
                  <div><Label className="text-xs">Beschreibung (max 30)</Label>
                    <Input className="h-8" maxLength={30} value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="creative-description-input" /></div>
                </div>
                <div><Label className="text-xs">Link</Label>
                  <Input className="h-8" value={f.link} onChange={(e) => setF((s) => ({ ...s, link: e.target.value }))} data-testid="creative-link-input" /></div>
                <Button className="w-full gap-1.5" onClick={() => setStep(2)} disabled={!textsOk} data-testid="wizard-next-btn">
                  Weiter: Bild wählen <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3" data-testid="wizard-image-step">
                {topPickId && (
                  <button type="button" onClick={() => setF((s) => ({ ...s, media_id: topPickId }))}
                    className="w-full flex items-center gap-2 text-left text-xs bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 hover:bg-amber-100 transition-colors"
                    data-testid="ai-best-media-btn">
                    <Star className="w-4 h-4 text-amber-500 shrink-0" fill="currentColor" />
                    <span><b>KI-Empfehlung:</b> Das markierte Bild hat in bisherigen Anzeigen die meisten Klicks geholt ({clicksMap[topPickId]} Klicks, 90 Tage) — klicke hier zum Übernehmen</span>
                  </button>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  <button type="button" onClick={() => setF((s) => ({ ...s, media_id: "keep" }))}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${f.media_id === "keep" ? "border-blue-600 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                    data-testid="keep-image-tile">
                    {currentPicture ? (
                      <img src={currentPicture} alt="Aktuelles Bild" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center"><ImageIcon className="w-6 h-6 text-slate-400" /></div>
                    )}
                    <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-0.5 text-center">Aktuelles Bild</span>
                    {f.media_id === "keep" && <span className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                  </button>
                  {sortedMedia.slice(0, 30).map((m) => (
                    <button key={m.id} type="button" onClick={() => setF((s) => ({ ...s, media_id: m.id }))}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${f.media_id === m.id ? "border-blue-600 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"}`}
                      title={m.original_filename || m.filename || ""} data-testid={`media-tile-${m.id}`}>
                      {thumbs[m.id] ? (
                        <img src={thumbs[m.id]} alt={m.original_filename || ""} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
                      )}
                      {(clicksMap[m.id] || 0) > 0 && (
                        <span className="absolute top-1 left-1 bg-amber-400 text-amber-950 rounded-full text-[9px] font-semibold px-1.5 py-0.5 flex items-center gap-0.5" data-testid={`best-badge-${m.id}`}>
                          <Star className="w-2.5 h-2.5" fill="currentColor" /> {clicksMap[m.id]}
                        </span>
                      )}
                      {f.media_id === m.id && <span className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                    </button>
                  ))}
                </div>
                {bestMedia.length > 0 && <p className="text-[10px] text-slate-400">⭐ = Klicks aus bisherigen Anzeigen (letzte 90 Tage)</p>}
                {media.length === 0 && <p className="text-xs text-slate-500">Keine Bilder in der Mediathek dieser Firma — lade zuerst Bilder hoch.</p>}
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-1" onClick={() => setStep(1)} data-testid="wizard-back-btn"><ArrowLeft className="w-4 h-4" /> Zurück</Button>
                  <Button className="flex-1 gap-1.5" onClick={() => setStep(3)} data-testid="wizard-next-btn-2">
                    Weiter: Vorschau <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3" data-testid="wizard-preview-step">
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => setPreviewTab("fb")}
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${previewTab === "fb" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                    data-testid="preview-tab-fb">Facebook</button>
                  <button type="button" onClick={() => setPreviewTab("ig")}
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${previewTab === "ig" ? "bg-gradient-to-r from-pink-500 to-violet-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                    data-testid="preview-tab-ig">Instagram</button>
                  <span className="text-[10px] text-slate-400 ml-auto">So sieht deine Anzeige ungefähr aus</span>
                </div>

                {previewTab === "fb" ? (
                  <div className="border rounded-lg overflow-hidden bg-white shadow-sm" data-testid="fb-ad-preview">
                    <div className="p-3 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">f</div>
                        <div><p className="text-xs font-semibold">Deine Facebook-Seite</p><p className="text-[10px] text-slate-400">Gesponsert · 🌐</p></div>
                      </div>
                      <p className="text-xs mt-2 whitespace-pre-wrap">{f.message}</p>
                    </div>
                    {previewImg ? <img src={previewImg} alt="Anzeigenbild" className="w-full max-h-72 object-cover" /> : (
                      <div className="w-full h-32 bg-slate-100 flex items-center justify-center"><ImageIcon className="w-8 h-8 text-slate-300" /></div>
                    )}
                    <div className="bg-slate-100 p-2.5 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase text-slate-500 truncate">{domain}</p>
                        {f.headline && <p className="text-xs font-semibold truncate">{f.headline}</p>}
                        {f.description && <p className="text-[11px] text-slate-500 truncate">{f.description}</p>}
                      </div>
                      <span className="shrink-0 text-xs font-medium bg-slate-200 rounded px-3 py-1.5">Mehr dazu</span>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden bg-white shadow-sm max-w-sm mx-auto" data-testid="ig-ad-preview">
                    <div className="p-2.5 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-500 p-[2px]">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[10px] font-bold">IG</div>
                      </div>
                      <div><p className="text-xs font-semibold leading-tight">dein_account</p><p className="text-[9px] text-slate-400 leading-tight">Gesponsert</p></div>
                    </div>
                    {previewImg ? <img src={previewImg} alt="Anzeigenbild" className="w-full aspect-square object-cover" /> : (
                      <div className="w-full aspect-square bg-slate-100 flex items-center justify-center"><ImageIcon className="w-8 h-8 text-slate-300" /></div>
                    )}
                    <div className="bg-blue-600 text-white text-xs font-medium px-3 py-2 flex items-center justify-between">
                      <span>Mehr dazu</span><ArrowRight className="w-3.5 h-3.5" />
                    </div>
                    <div className="p-2.5 space-y-1.5">
                      <div className="flex items-center gap-3 text-slate-700">
                        <Heart className="w-4 h-4" /><MessageCircle className="w-4 h-4" /><Send className="w-4 h-4" /><Bookmark className="w-4 h-4 ml-auto" />
                      </div>
                      <p className="text-xs"><b>dein_account</b> <span className="whitespace-pre-wrap">{f.message}</span></p>
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-slate-500">Beim Speichern erstellt Meta eine neue Anzeigen-Version und prüft sie kurz (Review, meist Minuten bis wenige Stunden).</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-1" onClick={() => setStep(2)} data-testid="wizard-back-btn-2"><ArrowLeft className="w-4 h-4" /> Zurück</Button>
                  <Button className="flex-1 gap-1.5" onClick={save} disabled={saving || !textsOk} data-testid="creative-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Anzeige speichern
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
